import { CKChartLine } from '@clearkit/react';
import pick from 'lodash/pick';
import xor from 'lodash/xor';
import { useMemo } from 'react';

import SalesPipelineSyncStatus from '~/components/audiences/SalesPipelineSyncStatus';
import { StageList } from '~/components/audiences/StageList';
import { StageListSkeleton } from '~/components/audiences/StageListSkeleton';
import { stages as stagesArray } from '~/lib/salesPipeline';

import styles from './styles';

const stagesByKey = stagesArray.reduce((object, stage) => {
  object[stage.key] = stage;

  return object;
}, {});

const ChartSkeleton = () => {
  return (
    <div>
      <div className="px-4 pt-4 pb-2 space-y-4">
        <div className="h-48 ck-skeleton"></div>
      </div>
    </div>
  );
};

export const Chart = ({ data }) => {
  const [seriesData, maxY] = useMemo(() => {
    let maxVal = 0;
    return [
      Object.keys(data).map((stageKey) => {
        const timeseries = data[stageKey].timeseries.map(
          ({ date, influencedValue }) => {
            maxVal = Math.max(maxVal, influencedValue);
            return {
              x: new Date(date).toLocaleDateString(),
              y: influencedValue,
            };
          },
        );

        return {
          id: stagesByKey[stageKey].label,
          data: timeseries,
          chartColor: stagesByKey[stageKey].chartColor,
        };
      }),
      maxVal,
    ];
  }, [data]);

  const chartColors = seriesData.map((i) => i.chartColor);

  const isEmpty =
    seriesData.filter((series) => series.data.length === 0).length ===
    seriesData.length;

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center h-48">
        <span className="font-medium text-center text-gray">No data</span>
      </div>
    );
  }

  return (
    <CKChartLine
      className="h-60"
      chartColors={chartColors}
      data={seriesData}
      toolTipValueFormat={(val) => val}
      toolTipHideTotal
      yScale={{
        max: maxY * 1.1,
        min: 0,
        reverse: false,
        stacked: false,
        type: 'linear',
      }}
    />
  );
};

export const SalesPipelineSVGDefs = () => {
  return (
    <svg
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        visibility: 'hidden',
      }}>
      <defs>
        {stagesArray.map((stage) => (
          <filter
            id={`line-shadow-${stage.key}`}
            key={stage.key}
            x="-50%"
            y="-10%"
            width="200%"
            height="200%">
            <feDropShadow
              floodColor={stage.accent.hex}
              dx="0"
              stdDeviation="1.5"
              floodOpacity={0.5}
            />
          </filter>
        ))}

        <linearGradient id="line-fade">
          <stop offset="0%" sytle={{ stopColor: 'red', stopOpacity: 1 }} />
          <stop offset="50%" sytle={{ stopColor: 'green', stopOpacity: 0.5 }} />
          <stop offset="100%" sytle={{ stopColor: 'blue', stopOpacity: 0 }} />
        </linearGradient>
        <linearGradient id="axis-fade" gradientTransform="rotate(90)">
          <stop offset="0%" style={{ stopColor: '#fff', stopOpacity: 0 }} />
          <stop offset="80%" style={{ stopColor: '#fff', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#fff', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            floodColor="black"
            dx="0"
            dy="0"
            stdDeviation=".5"
            floodOpacity={0.25}
          />
        </filter>
      </defs>
    </svg>
  );
};

const DateWindowMessage = ({ dateWindow, loading }) => {
  let text = '';

  switch (dateWindow) {
    case 'trailing_7_days':
      text = 'Audience performance this week';
      break;
    case 'trailing_14_days':
      text = 'Audience performance the past two weeks';
      break;
    case 'trailing_30_days':
      text = 'Audience performance this month';
      break;
  }

  return (
    <span
      className={classnames('font-medium text-gray-600 text-sm', {
        'ck-skeleton': loading,
      })}>
      {text}
    </span>
  );
};

export const SalesPipeline = ({ classes, loading, data, dateWindow }) => {
  const { stages, status, syncedAt } = data || {};

  const [activeStageKeys, setActiveStageKeys] = useState([
    'opportunities',
    'dealsWon',
  ]);
  const acceptableStagekeys = Object.keys(stagesByKey);

  let enabledStages = pick(stages, acceptableStagekeys);

  if (activeStageKeys.length !== 0) {
    enabledStages = pick(stages, activeStageKeys);
  }

  const toggleActiveStage = (key) => {
    setActiveStageKeys(xor([key], activeStageKeys));
  };

  return (
    <>
      <div className={classes.salesPipeline}>
        <div className="flex-1">
          {loading ? <ChartSkeleton /> : <Chart data={enabledStages} />}
        </div>
      </div>
      <div className="flex flex-col p-4 pt-0 overflow-auto border-b border-gray-200 space-y-3 h-36">
        <DateWindowMessage loading={loading} dateWindow={dateWindow} />
        {loading ? (
          <StageListSkeleton />
        ) : (
          <StageList
            data={stages}
            activeStageKeys={activeStageKeys}
            toggleStage={toggleActiveStage}
          />
        )}
      </div>
      <SalesPipelineSyncStatus
        status={status}
        syncedAt={syncedAt}
        loading={loading}
      />
      <SalesPipelineSVGDefs />
    </>
  );
};

export default withStyles(styles)(SalesPipeline);
